import { Vector3 } from "@babylonjs/core";
import type { BaitId, WeaponId } from "../inventory/ItemDefinitions";

export type ImplementedGhostId = "toyol" | "polong" | "pocong" | "penanggal" | "pontianak";
export type GhostId = ImplementedGhostId;
export type GhostMovementType = "ground" | "floating";
// The active compressed Kuntilanak mesh's nose/chest geometry faces authored
// +Z. Babylon's glTF conversion preserves that horizontal direction, so no
// extra yaw is needed below FacingRoot.
export const PONTIANAK_AUTHORED_FORWARD = new Vector3(0,0,1);
export const PONTIANAK_MODEL_AXIS_YAW = 0;

export interface WeaponReaction {
  readonly damage: number;
  readonly message: string;
  readonly reaction: "hit" | "wet" | "slip" | "dodge" | "flinch";
}

export interface GhostDialogues {
  readonly bait: string;
  readonly firstHit: string;
  readonly captureExpired: string;
}

export interface GhostGameplayGeometry {
  readonly colliderHeight: number;
  readonly colliderRadius: number;
  readonly colliderCenterY: number;
  readonly capturePointY: number;
  readonly markerPointY: number;
  readonly baitFallback: Readonly<{ x: number; y: number; z: number }>;
  readonly handNodeName?: string;
  readonly handOffset?: Readonly<{ x: number; y: number; z: number }>;
}

export interface GhostDefinition {
  readonly id: GhostId;
  readonly displayName: string;
  readonly description: string;
  readonly implemented: boolean;
  readonly modelPath?: string;
  readonly maxHealth: number;
  readonly effectiveBait: BaitId | "pulut-panggang";
  readonly effectiveWeapon: WeaponId;
  readonly captureWindowSeconds: number;
  readonly movementType: GhostMovementType;
  readonly movementSpeed: number;
  readonly detectionRadius: number;
  readonly attackReaction: "flee" | "flicker" | "flinch";
  readonly dialogues: GhostDialogues;
  readonly modelOrientation: Readonly<{ x: number; y: number; z: number }>;
  readonly modelScale: number;
  readonly scale: number;
  readonly preferredBait: BaitId | "pulut-panggang";
  readonly baitDetectionDistance: number;
  readonly hidingDurationSeconds: number;
  readonly hoverHeight: number;
  readonly gameplayGeometry?: GhostGameplayGeometry;
  readonly animationConfiguration: Readonly<{ playEmbedded: boolean; procedural: "toyol" | "flame" | "spectral" | "pocong" | "penanggal" }>;
  readonly audioConfiguration: Readonly<{ spatialCue: string; cueIntervalMin: number; cueIntervalMax: number }>;
  readonly idleDurationSeconds: number;
  readonly interactionDurationSeconds: number;
  readonly weaponReactions: Readonly<Record<WeaponId, WeaponReaction>>;
}

const reactionMap = (effective: WeaponId, reaction: WeaponReaction["reaction"]): Readonly<Record<WeaponId, WeaponReaction>> => ({
  slingshot: { damage: effective === "slingshot" ? 1 : 0, message: "Serangan itu tidak berkesan.", reaction: effective === "slingshot" ? reaction : "dodge" },
  "water-balloon": { damage: effective === "water-balloon" ? 1 : 0, message: "Belon air itu tidak berkesan.", reaction: effective === "water-balloon" ? reaction : "wet" },
  "banana-peel": { damage: effective === "banana-peel" ? 1 : 0, message: "Kulit pisang itu tidak berkesan.", reaction: effective === "banana-peel" ? reaction : "slip" },
  "mosquito-spray": { damage: effective === "mosquito-spray" ? 1 : 0, message: "Semburan itu tidak berkesan.", reaction: effective === "mosquito-spray" ? reaction : "dodge" },
  nail: { damage: effective === "nail" ? 1 : 0, message: "Paku itu tidak berkesan.", reaction: effective === "nail" ? reaction : "dodge" },
});

export const GHOST_DEFINITIONS: readonly GhostDefinition[] = [
  {
    id: "toyol", displayName: "Toyol", description: "Si kecil hijau yang sangat suka duit syiling.", implemented: true,
    modelPath: "./assets/ghosts/Toyol.glb", maxHealth: 3,
    effectiveBait: "coins", effectiveWeapon: "slingshot", captureWindowSeconds: 5, movementType: "ground",
    movementSpeed: 2.25, detectionRadius: 15, attackReaction: "flee",
    dialogues: { bait: "Duit! Duit!", firstHit: "Wadidis!", captureExpired: "Haha, lambat!" },
    modelOrientation: { x: 0, y: 0, z: 0 }, modelScale: 0.96, scale: 0.96, preferredBait: "coins", baitDetectionDistance: 15, hidingDurationSeconds: 2.5, hoverHeight: 0,
    animationConfiguration: { playEmbedded: false, procedural: "toyol" },
    audioConfiguration: { spatialCue: "rustle", cueIntervalMin: 8, cueIntervalMax: 15 },
    idleDurationSeconds: 10, interactionDurationSeconds: 4.2, weaponReactions: reactionMap("slingshot", "hit"),
  },
  {
    id: "polong", displayName: "Polong", description: "Api kecil ghaib yang suka menumpang nyalaan lilin.", implemented: true,
    modelPath: "./assets/ghosts/fire.glb", maxHealth: 3,
    effectiveBait: "candle", effectiveWeapon: "water-balloon", captureWindowSeconds: 5, movementType: "floating",
    movementSpeed: 1.9, detectionRadius: 17, attackReaction: "flicker",
    dialogues: { bait: "Wow boleh topup", firstHit: "oi ape ni oi?", captureExpired: "nanti kite repeat" },
    modelOrientation: { x: 0, y: 0, z: 0 }, modelScale: 0.68, scale: 0.68, preferredBait: "candle", baitDetectionDistance: 17, hidingDurationSeconds: 0, hoverHeight: 0.9,
    animationConfiguration: { playEmbedded: true, procedural: "flame" },
    audioConfiguration: { spatialCue: "rustle", cueIntervalMin: 10, cueIntervalMax: 17 },
    idleDurationSeconds: 4.5, interactionDurationSeconds: 12.5, weaponReactions: reactionMap("water-balloon", "wet"),
  },
  {
    id: "pontianak", displayName: "Pontianak", description: "Hantu wanita yang tidak akan melepaskan peluang untuk bercermin.", implemented: true,
    modelPath: "/assets/ghosts/kuntilanak.glb", maxHealth: 5,
    effectiveBait: "mirror", effectiveWeapon: "nail", captureWindowSeconds: 5, movementType: "floating",
    movementSpeed: 1.55, detectionRadius: 18, attackReaction: "flinch",
    dialogues: { bait: "kelas kau jah", firstHit: "ingat aku papan ke?", captureExpired: "jantan tak guna!" },
    modelOrientation: { x: 0, y: PONTIANAK_MODEL_AXIS_YAW, z: 0 }, modelScale: 1.72, scale: 1.72, preferredBait: "mirror", baitDetectionDistance: 18, hidingDurationSeconds: 0, hoverHeight: 0.24,
    gameplayGeometry: { colliderHeight: 1.72, colliderRadius: 0.31, colliderCenterY: 0.86, capturePointY: 0.93, markerPointY: 1.94, baitFallback: { x: -0.6, y: 1.36, z: 0.08 }, handNodeName: "RightHand", handOffset: { x: 0, y: -0.02, z: 0.08 } },
    animationConfiguration: { playEmbedded: false, procedural: "spectral" },
    audioConfiguration: { spatialCue: "rustle", cueIntervalMin: 11, cueIntervalMax: 18 },
    idleDurationSeconds: 6, interactionDurationSeconds: 5, weaponReactions: reactionMap("nail", "flinch"),
  },
  {
    id: "pocong", displayName: "Pocong", description: "Hantu berbalut yang terlalu gemar melantun.", implemented: true,
    modelPath: "/assets/ghosts/pocong_scary.glb", maxHealth: 4, effectiveBait: "trampoline", effectiveWeapon: "banana-peel", captureWindowSeconds: 5,
    movementType: "ground", movementSpeed: 1.6, detectionRadius: 15, attackReaction: "flinch",
    dialogues: { bait: "jump around!", firstHit: "", captureExpired: "" }, modelOrientation: { x: 0, y: 0, z: 0 },
    modelScale: 1.72, scale: 1.72, preferredBait: "trampoline", baitDetectionDistance: 15, hidingDurationSeconds: 0, hoverHeight: 0, animationConfiguration: { playEmbedded: false, procedural: "pocong" },
    audioConfiguration: { spatialCue: "rustle", cueIntervalMin: 10, cueIntervalMax: 18 }, idleDurationSeconds: 6,
    interactionDurationSeconds: 4, weaponReactions: reactionMap("banana-peel", "slip"),
  },
  {
    id: "penanggal", displayName: "Penanggal", description: "Kepala terbang yang tertarik pada darah palsu.", implemented: true,
    modelPath: "/assets/ghosts/krasue_eyes.glb", maxHealth: 4, effectiveBait: "fake-blood", effectiveWeapon: "mosquito-spray", captureWindowSeconds: 5,
    movementType: "floating", movementSpeed: 1.8, detectionRadius: 17, attackReaction: "flee",
    dialogues: { bait: "", firstHit: "", captureExpired: "" }, modelOrientation: { x: 0, y: 0, z: 0 },
    modelScale: 1.15, scale: 1.15, preferredBait: "fake-blood", baitDetectionDistance: 17, hidingDurationSeconds: 0, hoverHeight: 1.15, animationConfiguration: { playEmbedded: false, procedural: "penanggal" },
    gameplayGeometry: { colliderHeight: 1.15, colliderRadius: 0.38, colliderCenterY: 0.575, capturePointY: 0.575, markerPointY: 1.34, baitFallback: { x: 0, y: 0.575, z: 0 } },
    audioConfiguration: { spatialCue: "rustle", cueIntervalMin: 10, cueIntervalMax: 18 }, idleDurationSeconds: 6,
    interactionDurationSeconds: 4, weaponReactions: reactionMap("mosquito-spray", "hit"),
  },
];

export const GHOST_DEFINITION_BY_ID = Object.fromEntries(GHOST_DEFINITIONS.map((definition) => [definition.id, definition])) as Readonly<Record<GhostId, GhostDefinition>>;
export const ACTIVE_GHOST_IDS: readonly ImplementedGhostId[] = ["toyol", "polong", "pocong", "penanggal", "pontianak"];
export const TOYOL_DEFINITION = GHOST_DEFINITION_BY_ID.toyol;
export const POLONG_DEFINITION = GHOST_DEFINITION_BY_ID.polong;
export const PONTIANAK_DEFINITION = GHOST_DEFINITION_BY_ID.pontianak;

export const PONTIANAK_BAIT_CONFIGURATION = { preferredBait: "mirror" as const satisfies BaitId, noticeDialogue: "kelas kau jah" } as const;
export const GHOST_ENCOUNTER_CONFIGURATIONS = {
  pocong: { baitId: "trampoline", baitName: "Trampoline", baitDiscoveryDialogue: "jump around!", weapon: "banana-peel" },
  pontianak: { baitId: "mirror", baitName: "Cermin", baitDiscoveryDialogue: "kelas kau jah", weapon: "nail" },
  polong: { baitId: "candle", baitName: "Lilin", baitDiscoveryDialogue: "Wow boleh topup", weapon: "water-balloon" },
  penanggal: { baitId: "fake-blood", baitName: "Paket Darah Palsu", weapon: "mosquito-spray" },
  toyol: { baitId: "coins", baitName: "Syiling", baitDiscoveryDialogue: "Duit! Duit!", weapon: "slingshot" },
} as const;
